import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { Unsubscribe } from 'router5/dist/types/base';
import { RouteParams, Routes } from '../../../../../routes-table';
import { IScheduledZoomWebinarDetailsDTO, IZoomDAO } from '../../../../../api/zoom.dao';
import { IRouter } from '../../../../../infrastructure/router';
import { IErrorParsing } from '../../../../../infrastructure/error-parsing';
import { IDateTimeFormatter } from '../../../../../infrastructure/date-formatter';
import { RADIX_PARAM } from '../../../../../constants/common.constants';

interface ZoomMeetingWebinarWithDateFields extends IScheduledZoomWebinarDetailsDTO {
    startTime: Date;
    endTime: Date;
    inviteLink: string;
    inviteLinkWithPassword: string;
}

class ScheduledMeetingWebinarDetailsViewModel {
    public static getParsedJoinUrl(url: string): URL | null {
        if (!url) return null;
        try {
            return new URL(url);
        } catch {
            return null;
        }
    }

    @mobx.observable
    private currentMeetingId: number;
    @mobx.observable
    private currentMeetingWebinarDetails: ZoomMeetingWebinarWithDateFields;
    @mobx.observable
    private loading: boolean;
    @mobx.observable
    private error: string | null = null;
    private subscriptions: Unsubscribe[];

    constructor(
        @IoC.Inject private readonly zoomDao: IZoomDAO,
        @IoC.Inject private readonly router: IRouter<RouteParams>,
        @IoC.Inject private readonly errorParsing: IErrorParsing,
        @IoC.Inject private readonly dateTimeFormatter: IDateTimeFormatter
    ) {
        this.routeListeners();
    }

    public getCurrentMeetingWebinarDetails = () => {
        return this.currentMeetingWebinarDetails;
    };

    public isLoading = () => {
        return this.loading;
    };

    public getError = () => {
        return this.error;
    };

    public getCurrentMeetingId = () => {
        return this.currentMeetingId;
    };

    @mobx.action
    public setCurrentMeetingId = (value: number) => {
        this.currentMeetingId = value;
    };

    private routeListeners() {
        this.subscriptions = [];

        this.subscriptions.push(this.router.listen(
            Routes.ZoomScheduledMeetingWebinarDetails,
            routeArgs => {
                mobx.runInAction(() => {
                    this.currentMeetingId = Number.parseInt(routeArgs.meetingId, RADIX_PARAM);
                    this.fetchScheduledMeetingWebinarDetails();
                });
            }
        ));
    }

    @mobx.action
    public fetchScheduledMeetingWebinarDetails = async () => {
        this.currentMeetingWebinarDetails = null;
        this.loading = true;

        try {
            const currentMeetingWebinarDetails = await this.zoomDao.getScheduledWebinarDetails(this.currentMeetingId);

            const joinUrl = ScheduledMeetingWebinarDetailsViewModel.getParsedJoinUrl(currentMeetingWebinarDetails.joinUrl);

            const linkPassword = joinUrl ? joinUrl.searchParams.get('pwd') : null;

            if (linkPassword) joinUrl.searchParams.delete('pwd');

            const inviteLink = linkPassword ? joinUrl.href : currentMeetingWebinarDetails.joinUrl;

            mobx.runInAction(() => {
                this.currentMeetingWebinarDetails = {
                    ...currentMeetingWebinarDetails,
                    startTime: currentMeetingWebinarDetails.startTime,
                    endTime: currentMeetingWebinarDetails.endTime,
                    inviteLink: inviteLink,
                    inviteLinkWithPassword: linkPassword ? currentMeetingWebinarDetails.joinUrl : null
                };
            });
        } catch (e) {
            mobx.runInAction(() => {
                this.error = this.errorParsing.extractAPIError(e).errorMessage;
                if (e.response.status === 404) {
                    this.router.navigate(Routes.ZoomScheduledMeetings, {});
                } else {
                    throw e;
                }
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };
}

export { ScheduledMeetingWebinarDetailsViewModel, ZoomMeetingWebinarWithDateFields };
